import { useState, useEffect } from "react";
import Card from "react-bootstrap/Card";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import { useFormContext } from "react-hook-form";
import { useEventCities } from "../hooks/useEventCities";
import { formatCity } from "../utils/formatCity";
import { formatDate } from "../utils/formatDate";
import { schedulesList, EVENT_ID_MAP } from "../schedules";
import { PACKAGE_TYPES, PACKAGE_LABELS } from "../packageTypes";

const ScheduleSelector = () => {

  const { watch, setValue } = useFormContext();

  const schedules = watch("schedules") || [];

  const [activeTab, setActiveTab] = useState(0);

  const activeScheduleName = schedulesList[activeTab];
  const activeEventId = EVENT_ID_MAP[activeScheduleName];

  const { cities: citiesData, isLoading: isCitiesLoading, eventsData } = useEventCities(activeEventId);

  useEffect(() => {
    if (!isCitiesLoading && eventsData) {
      const firstAvailableIndex = schedulesList.findIndex((name) => {
        const id = EVENT_ID_MAP[name];
        return !eventsData[id]?.isExpired;
      });

      const currentActiveId = EVENT_ID_MAP[schedulesList[activeTab]];
      if (eventsData[currentActiveId]?.isExpired && firstAvailableIndex !== -1) {
        setActiveTab(firstAvailableIndex);
      }
    }
  }, [isCitiesLoading, eventsData, activeTab]);

  const schedule = schedules[activeTab] || {};
  const packageType = schedule.packageType || "";
  const selectedCities = schedule.cities || [];

  const allCitiesSelected =
    selectedCities.length === citiesData.length &&
    citiesData.length > 0;

  const PACKAGE_OPTIONS = [
    { key: "REGULAR", value: PACKAGE_TYPES.REGULAR },
    { key: "TRIAL", value: PACKAGE_TYPES.TRIAL },
    { key: "PLATINUM", value: PACKAGE_TYPES.PLATINUM }
  ];

  const updatePackage = (packageValue) => {
    setValue(`schedules.${activeTab}`, {
      packageType: packageValue,
      cities: []
    });
  };

  const updateCities = (cities) => {

    setValue(`schedules.${activeTab}.cities`, cities);

  };

  const resetCurrentTab = () => {
    setValue(`schedules.${activeTab}`, {
      packageType: "",
      cities: []
    });
  };

  return (
    <div className="schedule-confirm-wrapper bg-white p-3 mt-4 rounded shadow-sm">
      <Card className="p-0">
        <div>
          <h5 className="font-semibold text-lg">Schedule Selection</h5>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(Number(k))}
            className="mb-0"
          >
            {schedulesList.map((scheduleName, index) => {
              const eventId = EVENT_ID_MAP[scheduleName];
              const isExpired = eventsData[eventId]?.isExpired || false;

              return (
                <Tab
                  key={scheduleName}
                  eventKey={index}
                  disabled={isExpired}
                  title={
                    <span>
                      <strong>{scheduleName}</strong>
                      {isExpired && <span className="text-danger ms-1 text-xs" style={{ fontSize: "10px" }}>(Expired)</span>}
                    </span>
                  }
                />
              );
            })}
          </Tabs>
        </div>

        <Card.Body className="p-3">
          <div>
            <div className="d-flex justify-content-between">
              <p className="mb-1">
                <strong className="text-sm">Package Type</strong>
              </p>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => resetCurrentTab()}
              >
                Reset
              </button>
            </div>
            {PACKAGE_OPTIONS.map(({ value }) => (
              <div className="form-check" key={value}>
                <input
                  type="radio"
                  className="form-check-input"
                  checked={packageType === value}
                  onChange={() => updatePackage(value)}
                />
                <label className="form-check-label">
                  {PACKAGE_LABELS[value]}
                </label>
              </div>
            ))}

          </div>

          {packageType === PACKAGE_TYPES.REGULAR && (
            <div className="py-2">
              <div className="mb-3 p-2 bg-light rounded">
                <p className="text-sm mb-0">All 8 Cities: AUD 10.880</p>
                <p className="text-sm">Per City: AUD 1.700</p>
              </div>
            </div>
          )}

          {packageType === PACKAGE_TYPES.TRIAL && (
            <div className="py-2">
              <div className="mb-3 p-2 bg-light rounded">
                <p className="text-sm mb-0">All 8 Cities: AUD 7.680</p>
                <p className="text-sm">Per City: AUD 1.200</p>
              </div>
            </div>
          )}

          {packageType === PACKAGE_TYPES.PLATINUM && (
            <div className="p-2 bg-light rounded">
              <p className="text-sm mb-0">
                Twice a year in all cities
              </p>
              <p className="text-sm">
                Price: AUD 15.000
              </p>
            </div>
          )}

          {/* CITIES */}

          {packageType !== PACKAGE_TYPES.PLATINUM && (
            <div className="border-top mt-3 pt-2">
              <p className="mb-1">
                <strong className="text-sm">Cities</strong>
              </p>
              {isCitiesLoading ? (
                <div className="py-3 text-center">
                  <div className="spinner-border spinner-border-sm text-secondary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ms-2 text-sm text-muted">Loading cities...</span>
                </div>
              ) : citiesData.length === 0 ? (
                <div className="py-3 text-muted text-sm">No cities available for this event.</div>
              ) : (
                <>
                  <div className="form-check py-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={allCitiesSelected}
                      onChange={(e) =>
                        updateCities(
                          e.target.checked
                            ? citiesData.map((c) => c.nama_kota)
                            : []
                        )
                      }
                    />

                    <label className="form-check-label">
                      <strong>All Cities</strong>
                    </label>
                  </div>

                  <div className="city-grid">
                    {citiesData.map((cityObj) => {
                      const city = cityObj.nama_kota;
                      const date = cityObj.date_kota;

                      return (
                        <div key={city} className="form-check city-item">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedCities.includes(city)}
                            onChange={(e) => {
                              const updated = e.target.checked
                                ? [...selectedCities, city]
                                : selectedCities.filter((c) => c !== city);

                              updateCities(updated);
                            }}
                          />

                          <label className="form-check-label">
                            {formatCity(city)}
                          </label>
                          <div className="text-sm text-muted">
                            {formatDate(date)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

        </Card.Body>

      </Card>
    </div>
  );
};

export default ScheduleSelector;